import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Pencil, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TeamNameCellProps {
  teamId: number
  teamName: string
  onEditTeam: (teamId: number) => void
  onDeleteTeam: (teamId: number) => void
  isUpdating?: boolean
  className?: string
  isReadOnly?: boolean
}

const TeamNameCell: React.FC<TeamNameCellProps> = React.memo(
  ({
    teamId,
    teamName,
    onEditTeam,
    onDeleteTeam,
    isUpdating = false,
    className,
    isReadOnly = false,
  }) => {
    const [isHovered, setIsHovered] = useState(false)

    const handleEditClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      onEditTeam(teamId)
    }

    const handleDeleteClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      onDeleteTeam(teamId)
    }

    const handleMouseEnter = () => {
      setIsHovered(true)
    }

    const handleMouseLeave = () => {
      setIsHovered(false)
    }

    return (
      <div
        className={cn(
          'group flex items-center justify-between gap-2 min-w-0',
          className
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={cn(
            'truncate flex-1 transition-colors',
            !isReadOnly && 'cursor-pointer hover:text-blue-600'
          )}
          title={isReadOnly ? teamName : `Click to edit "${teamName}"`}
          onClick={!isReadOnly ? () => onEditTeam?.(teamId) : undefined}
        >
          {teamName}
        </div>
        {!isReadOnly && isHovered && (
          <div className='flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0'>
            <Button
              size='icon'
              variant='ghost'
              className='h-6 w-6 hover:bg-blue-100 hover:text-blue-700'
              onClick={handleEditClick}
              disabled={isUpdating}
              title='Edit team name'
            >
              <Pencil className='h-3 w-3' />
            </Button>
            <Button
              size='icon'
              variant='ghost'
              className='h-6 w-6 hover:bg-red-100 hover:text-red-700'
              onClick={handleDeleteClick}
              disabled={isUpdating}
              title='Delete team'
            >
              <Trash2 className='h-3 w-3' />
            </Button>
          </div>
        )}
      </div>
    )
  }
)

TeamNameCell.displayName = 'TeamNameCell'

export { TeamNameCell }
